// custom utility using fetch to send multipart/form-data to the backend
import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import { useState } from 'react';
import { useSnackbarContext } from '../../billing-tool/context/snackbar.context';
import { useGoogleFileUploadMutation } from '../../billing-tool/types';

interface UploadResponse {
  data: Promise<unknown>;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
}

export const useFileUpload = (): [
  (file: File, fileCategory: string) => Promise<UploadResponse>,
  (url: string, fileCategory: string) => Promise<UploadResponse>,
  { loading: boolean; error: boolean; data: unknown; errorMessage?: string },
] => {
  const { hingeHealthAuthState } = useHingeHealthSecurityContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [data, setData] = useState<unknown>(null);
  const [googleFileUpload] = useGoogleFileUploadMutation();
  const { setSnackbarState } = useSnackbarContext();

  const resetState = (): void => {
    setLoading(true);
    setError(false);
    setErrorMessage(undefined);
  };

  const uploadFile = async (
    file: File,
    fileCategory: string,
  ): Promise<UploadResponse> => {
    resetState();
    const token = hingeHealthAuthState?.accessToken?.accessToken;
    const BFF_URL = `${process.env.REACT_APP_BFF_ROOT}`;
    if (!token) {
      throw new Error('Unauthorized: No access token available');
    }

    if (!file) {
      throw new Error('No file provided');
    }

    if (file.type !== 'text/csv') {
      throw new Error('File must be a CSV file');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileCategory', fileCategory);

    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Security-Policy': `connect-src:  'self' ${process.env.REACT_APP_BFF_ROOT}/billing/file-upload`,
    };

    const fileUploadUrl = `${BFF_URL}/billing/file-upload`;

    try {
      setSnackbarState('Uploading...', 'info', 10000);
      const response = await fetch(fileUploadUrl, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData);
      return {
        data: Promise.resolve(responseData),
        loading: false,
        error: false,
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error uploading file';
      setError(true);
      setErrorMessage(message);
      return {
        data: Promise.resolve(null),
        loading: false,
        error: true,
        errorMessage: message,
      };
    } finally {
      setLoading(false);
    }
  };

  const uploadGoogleSheet = async (
    url: string,
    fileCategory: string,
  ): Promise<UploadResponse> => {
    resetState();

    if (!url || url.trim() === '') {
      const message = 'No Google Sheets URL provided';
      setError(true);
      setErrorMessage(message);
      setLoading(false);
      return {
        data: Promise.resolve(null),
        loading: false,
        error: true,
        errorMessage: message,
      };
    }

    try {
      setSnackbarState('Uploading...', 'info', 10000);
      const response = await googleFileUpload({
        variables: {
          fileInput: {
            fileLink: url,
            fileCategory,
          },
        },
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      setData(response.data);
      return {
        data: Promise.resolve(response.data),
        loading: false,
        error: false,
      };
    } catch (error) {
      let message =
        error instanceof Error ? error.message : 'Error uploading Google Sheet';
      if (message.includes('403')) {
        message =
          'Failed to upload file: Invalid google sheets url, check if file is in an accessible location';
      }
      setError(true);
      setErrorMessage(message);
      return {
        data: Promise.resolve(null),
        loading: false,
        error: true,
        errorMessage: message,
      };
    } finally {
      setLoading(false);
    }
  };

  return [
    uploadFile,
    uploadGoogleSheet,
    { loading, error, data, errorMessage },
  ];
};
